import * as _ from 'lodash';
import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { StatementMonthRequirement } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { FileUploader } from 'ng2-file-upload';
import { DocumentService } from 'ng2-app/modules/borrower/models/document/document.service';
import { Cookie } from 'ng2-cookies';
import { monthValueToFormattedMonth } from 'ng2-app/modules/shared/functions/strings';
import { Document } from 'ng2-app/modules/borrower/models/document/document.model';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { Observable, of, concat } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { environment } from 'environments/environment';

@Component({
    selector: 'month-requirement',
    templateUrl: 'month-requirement.component.html',
    styleUrls: ['month-requirement.component.scss']
})
export class MonthRequirementComponent implements OnInit {

    @Input() requirement: StatementMonthRequirement;
    @Input() borrowerId: number;

    uploader: FileUploader;

    @ViewChild('docSelectDropdown', { static: true })
    private docSelectDropdown: BsDropdownDirective;

    constructor(private documentService: DocumentService) {}

    ngOnInit() {
        this.initUploader();
    }

    initUploader () {
        this.uploader = new FileUploader({
            url: environment.API_URL + '/document',
            autoUpload: true,
            removeAfterUpload: true,
            method: 'POST',
            headers: [
                {name: 'Authorization', value: 'Bearer ' + Cookie.get('auth_token')}
            ],
            additionalParameter: {
                borrowerId: this.borrowerId,
                category: 'bankStatement',
                months: JSON.stringify([this.requirement.month])
            }
        });

        this.uploader.onAfterAddingFile = file => {
            file.withCredentials = false;
        };

        this.uploader.onCompleteItem = (item, response) => {
            const document = _.get(JSON.parse(response), 'data', null);
            if (!document) {
                console.error('Failed to create document.');
                return;
            }
            this.updatePreviousOnChange$().subscribe();
        };
    }

    docFilter(all: Document[]) {
        return all.filter((d) => (d.category === 'bankStatement' || d.category === null)
                && d.scanStatus !== 'INFECTED');
    }

    handleAttachment(documents: Document[]) {
        this.docSelectDropdown.hide();

        const document = _.get(documents, '0', null);

        if (!document) {
            console.error('Failed to select document.');
            return;
        }

        // Categorize document as bank statement
        document.category = 'bankStatement';

        // Set document month
        if (!document.months) {
            document.months = [];
        }
        document.months.push(this.requirement.month);

        // Prepare to save new document
        let save$ = this.documentService.save(document).pipe(map(() => {}));

        // Change classification of previous document if it existed
        if (this.requirement.documentId) {
            const updatePrevious$ = this.updatePreviousOnChange$();
            save$ = concat(save$, updatePrevious$);
        }

        // Save the document(s)
        save$.subscribe({
            next: () => {},
            error: (error) => {
                console.error('Failed to update requirement document!', error);
            }
          });
    }

    updatePreviousOnChange$(): Observable<void> {
        if (!this.requirement.documentId) {
            return of();
        }
        return this.documentService.get(this.requirement.documentId).pipe(
            switchMap((previous) => {
                if (
                    !previous.months
                    || !previous.months.includes(this.requirement.month)
                ) {
                    return of() as Observable<void>;
                }
                previous.months = previous.months.filter((m) => m !== this.requirement.month);
                return this.documentService.save(previous).pipe(map(() => {}));
            })
        );
    }

    attachLabel(requirement: StatementMonthRequirement) {
        return requirement.documentId ? 'Edit' : 'Add';
    }

    openDocument(documentId: number) {
        if (!this.docIsClean) {
            return;
        }

        const url = `${environment.API_URL}/document/${documentId}?stream=1&access_token=${Cookie.get('auth_token')}`;
        window.open(url, '_blank');
    }

    formattedMonth(month: string) {
        return monthValueToFormattedMonth(month);
    }

    formattedStatus(status: string) {
        return status.charAt(0).toUpperCase() + status.slice(1);
    }

    get viewHref() {
        return this.docIsClean ? '#' : null;
    }

    get viewTitle() {
        if (this.docIsInfected) {
            return 'Infected';
        }
        if (!this.docIsScanned) {
            return 'Scanning...';
        }
        return null;
    }

    get docIsScanned() {
        return this.requirement.documentId
            && this.requirement.documentScanned;
    }

    get docIsInfected() {
        return this.docIsScanned
            && this.requirement.documentInfected;
    }

    get docIsClean() {
        return this.docIsScanned
            && !this.requirement.documentInfected;
    }
}
